import { Box, ButtonBase } from '@/ui/primitives';
import { Text } from '@/ui/primitives';
import { Inline } from '../../../layout/Inline';
import { OverflowTooltip } from '../cells/OverflowTooltip';
import { cellSpacingSx } from './shared';
import type { SemanticColorKey } from '@/theme/semanticTokens';
import type { TypographyRole } from '@/theme/tokens';
import type { SxProps, Theme } from '@/ui/primitives';
import type { ReactElement } from 'react';

export type InlineListCellProps = {
  items: string[];
  onItemClick?: (item: string) => void;
  role?: TypographyRole;
  color?: SemanticColorKey;
  emptyLabel?: string;
  align?: 'left' | 'center' | 'right';
};

export type TagsInlineCellProps = { tags: string[]; onTagClick?: (tag: string) => void };

export const InlineListCell = ({
  items,
  onItemClick,
  role = 'body',
  color = 'textPrimary',
  emptyLabel = '-',
  align = 'left',
}: InlineListCellProps): ReactElement => {
  const justify = align === 'center' ? 'center' : align === 'right' ? 'flex-end' : 'flex-start';
  const textAlign = align === 'center' ? 'center' : align === 'right' ? 'right' : 'left';
  const buttonJustify = justify;
  if (items.length === 0) {
    return (
      <Text role={role} color="textSecondary">
        {emptyLabel}
      </Text>
    );
  }
  return (
    <Box sx={{ width: '100%', textAlign }}>
      <Inline gap="sm" wrap="wrap" justify={justify} align="center">
        {items.map((item, index) => {
          const label = index < items.length - 1 ? `${item},` : item;
          return (
            <ButtonBase
              key={item}
              onClick={
                onItemClick
                  ? () => {
                      onItemClick(item);
                    }
                  : undefined
              }
              sx={{ justifyContent: buttonJustify, alignItems: 'center', textAlign }}
            >
              <Text role={role} color={color}>
                {label}
              </Text>
            </ButtonBase>
          );
        })}
      </Inline>
    </Box>
  );
};

export const TagsInlineCell = ({ tags, onTagClick }: TagsInlineCellProps): ReactElement => {
  const containerSx = [
    cellSpacingSx,
    { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  ] as SxProps<Theme>;
  const label = tags.join(', ');
  if (tags.length === 0) {
    return (
      <Box sx={cellSpacingSx}>
        <Text role="body" color="textSecondary">
          -
        </Text>
      </Box>
    );
  }
  return (
    <Box sx={containerSx}>
      {onTagClick ? (
        <Inline gap="xs" wrap="nowrap" sx={{ overflow: 'hidden' }}>
          {tags.map((tag, index) => {
            const itemLabel = index < tags.length - 1 ? `${tag},` : tag;
            return (
              <ButtonBase
                key={tag}
                onClick={() => {
                  onTagClick(tag);
                }}
                sx={{
                  alignItems: 'center',
                  textAlign: 'left',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                }}
              >
                <Text role="body" color="textPrimary">
                  {itemLabel}
                </Text>
              </ButtonBase>
            );
          })}
        </Inline>
      ) : (
        <OverflowTooltip title={label} placement="bottom">
          <Text
            role="body"
            color="textPrimary"
            sx={{
              display: 'block',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {label}
          </Text>
        </OverflowTooltip>
      )}
    </Box>
  );
};
